import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLiveTracking, clearTrackingData } from "../../store/logistics/logistics.action";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";

const TrackingModal = ({ trackingId, onClose }) => {
  const dispatch = useDispatch();
  const { trackingData, loading } = useSelector((state) => state.logistics);

  useEffect(() => {
    if (trackingId) {
      dispatch(getLiveTracking(trackingId));
    }
    return () => {
      dispatch(clearTrackingData());
    };
  }, [dispatch, trackingId]);

  // Status colors matching logistics ParcelStatus enum
  const getStatusColor = (status) => {
    const statusColors = {
      pending: "#F0C420",
      pickup_assign: "#17a2b8",
      pickup_re_schedule: "#fd7e14",
      received_by_pickup_man: "#17a2b8",
      received_warehouse: "#6f42c1",
      transfer_to_hub: "#6f42c1",
      delivery_man_assign: "#007bff",
      delivery_re_schedule: "#fd7e14",
      delivered: "#28a745",
      deliver: "#28a745",
      return_warehouse: "#dc3545",
      assign_merchant: "#6c757d",
      returned_merchant: "#6c757d",
      received_by_hub: "#6f42c1",
      return_to_courier: "#dc3545",
      return_assign_to_merchant: "#dc3545",
      return_merchant_re_schedule: "#fd7e14",
      return_received_by_merchant: "#6c757d",
      partial_delivered: "#20c997",
    };
    return statusColors[status] || "#F0C420";
  };

  // Status labels matching logistics ParcelStatus enum
  const getStatusLabel = (status) => {
    const statusLabels = {
      pending: "Pending",
      pickup_assign: "Pickup Assigned",
      pickup_re_schedule: "Pickup Rescheduled",
      received_by_pickup_man: "Received by Pickup Man",
      received_warehouse: "Received at Warehouse",
      transfer_to_hub: "Transfer to Hub",
      delivery_man_assign: "Delivery Man Assigned",
      delivery_re_schedule: "Delivery Rescheduled",
      delivered: "Delivered",
      deliver: "Delivered",
      return_warehouse: "Return to Warehouse",
      assign_merchant: "Assigned to Merchant",
      returned_merchant: "Returned to Merchant",
      received_by_hub: "Received by Hub",
      return_to_courier: "Return to Courier",
      return_assign_to_merchant: "Return Assigned to Merchant",
      return_merchant_re_schedule: "Return Rescheduled",
      return_received_by_merchant: "Returned to Merchant",
      partial_delivered: "Partially Delivered",
      // Event types
      processing: "Processing",
      order_received: "Order Received by Logistics",
      received_at_hub: "Received at Hub",
      delivery_assigned: "Delivery Assigned",
      pickup_assigned: "Pickup Assigned",
      assigned_to_agent: "Assigned to Agent",
      assigned_to_regional: "Assigned to Regional Manager",
    };
    return statusLabels[status] || status || "Processing";
  };

  // Get icon color based on status
  const getStatusIconColor = (status) => {
    if (status === "delivered" || status === "deliver") return "#28a745";
    if (status === "partial_delivered") return "#20c997";
    if (status?.includes("return") || status?.includes("cancel")) return "#dc3545";
    return "#F0C420";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content"
          style={{
            backgroundColor: "#ffffff",
            color: "#1a1a1a",
            borderRadius: "16px",
            border: "2px solid #F0C420",
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
          }}
        >
          {/* Header */}
          <div
            className="modal-header"
            style={{
              borderBottom: "2px solid #F0C420",
              backgroundColor: "#1a1a1a",
              borderRadius: "14px 14px 0 0",
              padding: "16px 24px",
            }}
          >
            <h5
              className="modal-title d-flex align-items-center gap-2"
              style={{ color: "#F0C420", fontWeight: "600", fontSize: "18px" }}
            >
              <LocalShippingIcon style={{ color: "#F0C420" }} />
              Tracking Details
            </h5>
            <button
              type="button"
              className="btn btn-sm"
              onClick={onClose}
              style={{
                color: "#fff",
                backgroundColor: "transparent",
                border: "1px solid #F0C420",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CloseIcon style={{ fontSize: "18px" }} />
            </button>
          </div>

          {/* Body */}
          <div className="modal-body" style={{ padding: "24px" }}>
            {loading ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border"
                  role="status"
                  style={{ color: "#F0C420", width: "48px", height: "48px" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3" style={{ color: "#666" }}>
                  Fetching tracking information...
                </p>
              </div>
            ) : trackingData ? (
              <>
                {/* Tracking Header Card */}
                <div
                  className="p-4 mb-4 rounded"
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                    borderLeft: "4px solid #F0C420",
                  }}
                >
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <small style={{ color: "#888", fontSize: "12px", textTransform: "uppercase" }}>
                        Tracking ID
                      </small>
                      <h4
                        style={{
                          color: "#1a1a1a",
                          fontWeight: "700",
                          fontFamily: "monospace",
                          fontSize: "20px",
                          marginBottom: "0",
                        }}
                      >
                        {trackingData.trackingId}
                      </h4>
                    </div>
                    <div className="col-md-6 text-md-end">
                      <small style={{ color: "#888", fontSize: "12px", textTransform: "uppercase" }}>
                        Current Status
                      </small>
                      <div>
                        <span
                          style={{
                            backgroundColor: getStatusColor(trackingData.currentStatus),
                            color: trackingData.currentStatus === "pending" ? "#1a1a1a" : "#fff",
                            padding: "8px 20px",
                            borderRadius: "20px",
                            fontSize: "14px",
                            fontWeight: "600",
                            display: "inline-block",
                          }}
                        >
                          {getStatusLabel(trackingData.currentStatus)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <hr style={{ borderColor: "#e9ecef", margin: "16px 0" }} />
                  <div className="row">
                    <div className="col-md-6">
                      <small style={{ color: "#888", fontSize: "12px" }}>Created At</small>
                      <p style={{ color: "#1a1a1a", marginBottom: "0", fontWeight: "500" }}>
                        {formatDate(trackingData.createdAt)}
                      </p>
                    </div>
                    <div className="col-md-6 text-md-end">
                      <small style={{ color: "#888", fontSize: "12px" }}>Merchant</small>
                      <p style={{ color: "#1a1a1a", marginBottom: "0", fontWeight: "500" }}>
                        {trackingData.merchantName || "E-commerce System"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Customer Delivery Address Card */}
                <div
                  className="p-4 mb-4 rounded"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #e9ecef",
                    borderLeft: "4px solid #28a745",
                  }}
                >
                  <h6
                    style={{
                      color: "#1a1a1a",
                      fontWeight: "600",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <HomeIcon style={{ color: "#28a745", fontSize: "20px" }} />
                    Delivery Destination
                  </h6>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <PersonIcon style={{ color: "#666", fontSize: "18px" }} />
                        <div>
                          <small style={{ color: "#888", fontSize: "11px" }}>Customer Name</small>
                          <p style={{ color: "#1a1a1a", marginBottom: "0", fontWeight: "500" }}>
                            {trackingData.customerName || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <PhoneIcon style={{ color: "#666", fontSize: "18px" }} />
                        <div>
                          <small style={{ color: "#888", fontSize: "11px" }}>Phone</small>
                          <p style={{ color: "#1a1a1a", marginBottom: "0", fontWeight: "500" }}>
                            {trackingData.customerPhone || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-start gap-2 mt-2">
                        <LocationOnIcon style={{ color: "#F0C420", fontSize: "18px", marginTop: "2px" }} />
                        <div>
                          <small style={{ color: "#888", fontSize: "11px" }}>Delivery Address</small>
                          <p style={{ color: "#1a1a1a", marginBottom: "0", fontWeight: "500" }}>
                            {trackingData.customerAddress || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline Section */}
                <h6
                  style={{
                    color: "#1a1a1a",
                    fontWeight: "600",
                    marginBottom: "20px",
                    paddingBottom: "10px",
                    borderBottom: "2px solid #F0C420",
                    display: "inline-block",
                  }}
                >
                  Delivery Timeline
                </h6>

                {trackingData.events && trackingData.events.length > 0 ? (
                  <div
                    className="timeline"
                    style={{ maxHeight: "350px", overflowY: "auto", paddingRight: "10px" }}
                  >
                    {trackingData.events.map((event, index) => (
                      <div
                        key={event.id || index}
                        className="d-flex mb-3"
                        style={{ position: "relative" }}
                      >
                        {/* Timeline Line */}
                        {index < trackingData.events.length - 1 && (
                          <div
                            style={{
                              position: "absolute",
                              left: "15px",
                              top: "36px",
                              bottom: "-12px",
                              width: "2px",
                              backgroundColor: "#e9ecef",
                            }}
                          />
                        )}

                        {/* Timeline Icon */}
                        <div
                          className="me-3"
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: getStatusIconColor(event.eventType),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            zIndex: 1,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          }}
                        >
                          {trackingData.currentStatus === "delivered" && index === 0 ? (
                            <CheckCircleIcon style={{ fontSize: "18px", color: "#fff" }} />
                          ) : (
                            <AccessTimeIcon style={{ fontSize: "18px", color: "#1a1a1a" }} />
                          )}
                        </div>

                        {/* Timeline Content */}
                        <div
                          className="flex-grow-1 p-3 rounded"
                          style={{
                            backgroundColor: "#fff",
                            border: "1px solid #e9ecef",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <div>
                              <strong
                                style={{
                                  color: "#1a1a1a",
                                  fontSize: "14px",
                                }}
                              >
                                {getStatusLabel(event.eventType)}
                              </strong>
                              {event.hub && (
                                <p
                                  className="mb-1 d-flex align-items-center gap-1"
                                  style={{ fontSize: "13px", color: "#666" }}
                                >
                                  <LocationOnIcon style={{ fontSize: "14px", color: "#F0C420" }} />
                                  {event.hub}
                                  {event.hubAddress && ` - ${event.hubAddress}`}
                                  {event.hubPhone && (
                                    <span style={{ color: "#888", marginLeft: "8px" }}>Tel: {event.hubPhone}</span>
                                  )}
                                </p>
                              )}
                              {event.deliveryMan && (
                                <p
                                  className="mb-0 d-flex align-items-center gap-1"
                                  style={{ fontSize: "12px", color: "#888" }}
                                >
                                  <PersonIcon style={{ fontSize: "14px" }} />
                                  Rider: {event.deliveryMan}
                                  {event.deliveryManPhone && (
                                    <span style={{ color: "#F0C420" }}> ({event.deliveryManPhone})</span>
                                  )}
                                </p>
                              )}
                              {event.pickupMan && (
                                <p
                                  className="mb-0 d-flex align-items-center gap-1"
                                  style={{ fontSize: "12px", color: "#888" }}
                                >
                                  <PersonIcon style={{ fontSize: "14px" }} />
                                  Pickup: {event.pickupMan}
                                  {event.pickupManPhone && (
                                    <span style={{ color: "#F0C420" }}> ({event.pickupManPhone})</span>
                                  )}
                                </p>
                              )}
                              {event.createdBy && (
                                <p
                                  className="mb-0 d-flex align-items-center gap-1"
                                  style={{ fontSize: "12px", color: "#888" }}
                                >
                                  <PersonIcon style={{ fontSize: "14px" }} />
                                  By: {event.createdBy}
                                </p>
                              )}
                              {event.note && (
                                <p
                                  className="mb-0"
                                  style={{ fontSize: "12px", color: "#888", fontStyle: "italic" }}
                                >
                                  Note: {event.note}
                                </p>
                              )}
                            </div>
                            <small
                              style={{
                                color: "#888",
                                fontSize: "11px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatDate(event.timestamp)}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className="text-center py-4"
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderRadius: "12px",
                      border: "1px dashed #e9ecef",
                    }}
                  >
                    <AccessTimeIcon style={{ fontSize: "48px", color: "#F0C420", marginBottom: "16px" }} />
                    <p style={{ color: "#666", marginBottom: "4px" }}>No tracking events available yet.</p>
                    <small style={{ color: "#888" }}>The parcel is being processed.</small>
                  </div>
                )}
              </>
            ) : (
              <div
                className="text-center py-5"
                style={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "12px",
                }}
              >
                <LocalShippingIcon style={{ fontSize: "48px", color: "#ccc", marginBottom: "16px" }} />
                <p style={{ color: "#666" }}>Unable to fetch tracking information.</p>
                <small style={{ color: "#888" }}>Please try again later.</small>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="modal-footer"
            style={{
              borderTop: "1px solid #e9ecef",
              padding: "16px 24px",
              backgroundColor: "#f8f9fa",
              borderRadius: "0 0 14px 14px",
            }}
          >
            <button
              className="btn"
              onClick={onClose}
              style={{
                backgroundColor: "#fff",
                color: "#1a1a1a",
                border: "1px solid #1a1a1a",
                padding: "10px 24px",
                borderRadius: "8px",
                fontWeight: "500",
              }}
            >
              Close
            </button>
            <button
              className="btn"
              onClick={() => dispatch(getLiveTracking(trackingId))}
              disabled={loading}
              style={{
                backgroundColor: "#F0C420",
                color: "#1a1a1a",
                border: "none",
                padding: "10px 24px",
                borderRadius: "8px",
                fontWeight: "600",
              }}
            >
              {loading ? "Refreshing..." : "Refresh Status"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingModal;
